import React, { useState, useEffect } from 'react';
import { contactService } from '../../../services/contactService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactsTab = ({ practitionerId }) => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  useEffect(() => {
    if (practitionerId) {
      loadContacts();
    }
  }, [practitionerId]);

  useEffect(() => {
    if (searchTerm) {
      handleSearch();
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchTerm, contacts]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await contactService?.getContacts(practitionerId);
      setContacts(data);
      setFilteredContacts(data);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const results = await contactService?.searchContacts(practitionerId, searchTerm);
      setFilteredContacts(results);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la recherche');
    }
  };

  const handleExportCSV = () => {
    contactService?.downloadCSV(filteredContacts, `contacts_dentistes_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
  };

  const openNotesModal = (contact) => {
    setSelectedContact(contact);
    setNotesText(contact?.notes || '');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedContact(null);
    setNotesText('');
  };

  const handleSaveNotes = async () => {
    if (!selectedContact) return;

    try {
      setSavingNotes(true);
      await contactService?.updateContactNotes(selectedContact?.id, notesText);
      
      // Update local state
      setContacts(prev => prev?.map(c => 
        c?.id === selectedContact?.id ? { ...c, notes: notesText } : c
      ));
      
      closeNotesModal();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la sauvegarde des notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const openDeleteConfirm = (contact) => {
    setContactToDelete(contact);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;

    try {
      await contactService?.deleteContact(contactToDelete?.id);
      setContacts(prev => prev?.filter(c => c?.id !== contactToDelete?.id));
      closeDeleteConfirm();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la suppression du contact');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-primary">Répertoire des Contacts</h2>
          <p className="text-sm text-secondary mt-1">
            Contacts collectés automatiquement depuis vos adressages
          </p>
        </div>
        <Button
          onClick={handleExportCSV}
          disabled={filteredContacts?.length === 0}
          className="flex items-center gap-2"
        >
          <Icon name="Download" className="w-4 h-4" />
          Exporter CSV
        </Button>
      </div>
      {error && (
        <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {/* Search Bar */}
      <div className="relative">
        <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
        <input
          type="text"
          placeholder="Rechercher par nom, cabinet, email ou téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full pl-10 pr-4 py-3 bg-white text-gray-900 placeholder-gray-500 border border-white/12 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
        />
      </div>
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-accent/20 p-2 rounded-lg">
              <Icon name="Users" className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-sm text-secondary">Total Contacts</p>
              <p className="text-2xl font-bold text-primary">{contacts?.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-success/10 border border-success/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-success/20 p-2 rounded-lg">
              <Icon name="FileText" className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-secondary">Total Adressages</p>
              <p className="text-2xl font-bold text-primary">
                {contacts?.reduce((sum, c) => sum + (c?.totalReferrals || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-500/20 p-2 rounded-lg">
              <Icon name="TrendingUp" className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-secondary">Résultats Filtrés</p>
              <p className="text-2xl font-bold text-primary">{filteredContacts?.length}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Contacts Table */}
      {filteredContacts?.length === 0 ? (
        <div className="text-center py-12 bg-card-elevated rounded-lg border border-white/8">
          <Icon name="Users" className="w-12 h-12 text-muted mx-auto mb-3" />
          <p className="text-secondary">
            {searchTerm ? 'Aucun contact trouvé pour cette recherche' : 'Aucun contact disponible'}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-white/8 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/8">
              <thead className="bg-card-elevated">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Dentiste
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Cabinet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Première Réf.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Dernière Réf.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-white/8">
                {filteredContacts?.map((contact) => (
                  <tr key={contact?.id} className="hover:bg-card-hover transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-accent/20 rounded-full flex items-center justify-center">
                          <span className="text-accent font-semibold text-sm">
                            {contact?.dentistName?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">
                            {contact?.dentistName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary">{contact?.practiceName || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-secondary">
                        {contact?.email && (
                          <div className="flex items-center gap-1 mb-1">
                            <Icon name="Mail" className="w-3 h-3 text-muted" />
                            <a href={`mailto:${contact?.email}`} className="text-accent hover:underline">
                              {contact?.email}
                            </a>
                          </div>
                        )}
                        {contact?.phone && (
                          <div className="flex items-center gap-1">
                            <Icon name="Phone" className="w-3 h-3 text-muted" />
                            <a href={`tel:${contact?.phone}`} className="text-accent hover:underline">
                              {contact?.phone}
                            </a>
                          </div>
                        )}
                        {!contact?.email && !contact?.phone && '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted">
                      {formatDate(contact?.firstReferralDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted">
                      {formatDate(contact?.lastReferralDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-accent/20 text-accent">
                        {contact?.totalReferrals}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openNotesModal(contact)}
                          className="text-accent hover:text-accent/80 p-1 rounded hover:bg-accent/10 transition-colors"
                          title="Ajouter/Modifier notes"
                        >
                          <Icon name="FileText" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(contact)}
                          className="text-error hover:text-error/80 p-1 rounded hover:bg-error/10 transition-colors"
                          title="Supprimer contact"
                        >
                          <Icon name="Trash2" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card-elevated rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/12">
            <div className="p-6 border-b border-white/8">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-primary">
                  Notes - {selectedContact?.dentistName}
                </h3>
                <button
                  onClick={closeNotesModal}
                  className="text-muted hover:text-secondary transition-colors"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e?.target?.value)}
                placeholder="Ajoutez des notes sur ce contact..."
                rows={8}
                className="w-full px-4 py-3 bg-white text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
              />
            </div>
            <div className="p-6 border-t border-white/8 flex justify-end gap-3">
              <Button
                onClick={closeNotesModal}
                variant="outline"
                disabled={savingNotes}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="flex items-center gap-2"
              >
                {savingNotes ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Icon name="Save" className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card-elevated rounded-lg shadow-xl max-w-md w-full border border-white/12">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-error/20 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" className="w-6 h-6 text-error" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary">Confirmer la suppression</h3>
                  <p className="text-sm text-secondary mt-1">
                    Êtes-vous sûr de vouloir supprimer ce contact ?
                  </p>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 mb-4 border border-white/8">
                <p className="text-sm font-medium text-primary">{contactToDelete?.dentistName}</p>
                {contactToDelete?.practiceName && (
                  <p className="text-sm text-secondary">{contactToDelete?.practiceName}</p>
                )}
              </div>
              <p className="text-sm text-secondary mb-6">
                Cette action est irréversible. Les adressages existants ne seront pas affectés.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={closeDeleteConfirm}
                  variant="outline"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleDeleteContact}
                  className="bg-error hover:bg-error/90 text-white"
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;